import posthog from 'posthog-js';
import type { AnyRouter } from '@tanstack/react-router';
import { getAppRouteMeta } from 'src/routing/router-static-data';

export const ROUTE_RENDERED_EVENT = 'app.performance.route_rendered';
export const ROUTE_READY_EVENT = 'app.performance.route_ready';
export const DEFAULT_READINESS_TIMEOUT_MS = 15_000;

export type NavigationType = 'initial' | 'client';
export type RouteRenderedResult = 'success' | 'cancelled';
export type ReadinessMode = 'route-rendered' | 'manual';
export type RouteReadyOutcome = 'success' | 'error' | 'timeout' | 'cancelled';

export interface RenderedRouteInfo {
  routeId: string;
  pageId: string;
  readiness: ReadinessMode;
  readinessTimeoutMs?: number;
}

export interface RoutePerformanceEmitter {
  capture: (event: string, properties: Record<string, unknown>) => void;
  now: () => number;
  afterPaint: (callback: () => void) => void;
  schedule: (callback: () => void, delayMs: number) => () => void;
}

interface ActiveNavigation {
  start: number;
  navigationType: NavigationType;
  renderedEmitted: boolean;
  /** True once the route_rendered event has actually been captured (post-paint). */
  renderedCaptured: boolean;
  info: RenderedRouteInfo | null;
  /** Where a client navigation was headed, known before it renders. */
  destination: RenderedRouteInfo | null;
  readyEmitted: boolean;
  earlyReadyOutcome: RouteReadyOutcome | null;
  cancelReadinessTimeout: (() => void) | null;
}

const UNKNOWN_ROUTE: RenderedRouteInfo = {
  routeId: 'unknown',
  pageId: 'unknown',
  readiness: 'route-rendered',
};

/**
 * One navigation → exactly one route_rendered and at most one route_ready
 * event. Pure state machine so the dedup/cancel/timeout invariants are
 * unit-testable without a real router; wiring to router lifecycle events and
 * PostHog happens in initRoutePerformanceTelemetry.
 */
export function createRoutePerformanceTracker(
  emitter: RoutePerformanceEmitter,
) {
  let active: ActiveNavigation | null = null;

  function startNavigation(
    navigationType: NavigationType,
    destination: RenderedRouteInfo | null = null,
  ): ActiveNavigation {
    return {
      start: navigationType === 'initial' ? 0 : emitter.now(),
      navigationType,
      renderedEmitted: false,
      renderedCaptured: false,
      info: null,
      destination,
      readyEmitted: false,
      earlyReadyOutcome: null,
      cancelReadinessTimeout: null,
    };
  }

  function emitRendered(
    info: RenderedRouteInfo,
    nav: ActiveNavigation,
    result: RouteRenderedResult,
    durationMs: number,
  ): void {
    emitter.capture(ROUTE_RENDERED_EVENT, {
      page_id: info.pageId,
      route_id: info.routeId,
      navigation_type: nav.navigationType,
      duration_ms: Math.round(durationMs),
      result,
    });
  }

  function emitReady(
    nav: ActiveNavigation,
    outcome: RouteReadyOutcome,
    durationMs: number,
  ): void {
    if (nav.readyEmitted || !nav.info) return;
    nav.readyEmitted = true;
    nav.cancelReadinessTimeout?.();
    nav.cancelReadinessTimeout = null;
    emitter.capture(ROUTE_READY_EVENT, {
      page_id: nav.info.pageId,
      route_id: nav.info.routeId,
      navigation_type: nav.navigationType,
      readiness_mode: nav.info.readiness,
      duration_ms: Math.round(durationMs),
      outcome,
    });
  }

  return {
    onBeforeNavigate(destination: RenderedRouteInfo | null): void {
      // The interrupted navigation never rendered; report it against where it
      // was headed, not against the navigation that interrupted it.
      if (active && !active.renderedEmitted) {
        active.renderedEmitted = true;
        emitRendered(
          active.destination ?? UNKNOWN_ROUTE,
          active,
          'cancelled',
          emitter.now() - active.start,
        );
      }
      if (active && !active.readyEmitted && active.info) {
        emitReady(active, 'cancelled', emitter.now() - active.start);
      }
      active = startNavigation('client', destination);
    },

    onRendered(info: RenderedRouteInfo): void {
      // The initial document load renders without a preceding onBeforeNavigate;
      // now() is relative to timeOrigin, so start 0 measures the full cold load.
      const nav = active ?? startNavigation('initial');
      active = nav;
      if (nav.renderedEmitted) return;
      nav.renderedEmitted = true;
      nav.info = info;
      const durationMs = emitter.now() - nav.start;
      emitter.afterPaint(() => {
        emitRendered(info, nav, 'success', durationMs);
        nav.renderedCaptured = true;
        if (info.readiness === 'route-rendered') {
          emitReady(nav, 'success', durationMs);
        } else if (nav.earlyReadyOutcome) {
          emitReady(nav, nav.earlyReadyOutcome, emitter.now() - nav.start);
        } else {
          const timeoutMs =
            info.readinessTimeoutMs ?? DEFAULT_READINESS_TIMEOUT_MS;
          nav.cancelReadinessTimeout = emitter.schedule(() => {
            emitReady(nav, 'timeout', emitter.now() - nav.start);
          }, timeoutMs);
        }
      });
    },

    markReady(
      outcome: Exclude<RouteReadyOutcome, 'timeout' | 'cancelled'>,
    ): void {
      const nav = active;
      if (!nav || nav.readyEmitted) return;
      if (nav.info && nav.info.readiness !== 'manual') return;
      if (!nav.renderedCaptured) {
        // Data settled before route_rendered was captured (cached queries can
        // be ready before the paint boundary). Hold the outcome so ready is
        // always emitted after rendered, with duration ≥ the rendered one.
        nav.earlyReadyOutcome = outcome;
        return;
      }
      emitReady(nav, outcome, emitter.now() - nav.start);
    },
  };
}

export type RoutePerformanceTracker = ReturnType<
  typeof createRoutePerformanceTracker
>;

let activeTracker: RoutePerformanceTracker | null = null;

/** Called by usePageReady; a no-op until telemetry is initialized. */
export function notifyPageReady(outcome: 'success' | 'error'): void {
  activeTracker?.markReady(outcome);
}

function leafRouteInfo(
  router: AnyRouter,
  matches: ReadonlyArray<{ routeId: string }> | undefined,
): RenderedRouteInfo | null {
  const leaf = matches?.at(-1);
  if (!leaf) return null;
  const route = router.routesById[leaf.routeId] as
    { options?: { staticData?: unknown } } | undefined;
  const meta = route ? getAppRouteMeta(route)?.performance : undefined;
  return {
    routeId: leaf.routeId,
    pageId: meta?.pageId ?? leaf.routeId,
    readiness: meta?.readiness ?? 'route-rendered',
    ...(meta?.readiness === 'manual' && meta.readinessTimeoutMs !== undefined
      ? { readinessTimeoutMs: meta.readinessTimeoutMs }
      : {}),
  };
}

function captureRouteEvent(
  event: string,
  properties: Record<string, unknown>,
): void {
  if (!posthog.__loaded) return;
  posthog.capture(event, {
    ...properties,
    visibility: document.visibilityState,
  });
}

function afterNextPaint(callback: () => void): void {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

function scheduleTimeout(callback: () => void, delayMs: number): () => void {
  const handle = window.setTimeout(callback, delayMs);
  return () => window.clearTimeout(handle);
}

/**
 * Called once, outside React, right after createRouter — so StrictMode
 * double-mounting can never double-subscribe. Preloads never reach these
 * events: they load route code/data without committing a navigation.
 *
 * `emitterOverrides` is a test seam for the integration test.
 */
export function initRoutePerformanceTelemetry(
  router: AnyRouter,
  emitterOverrides?: Partial<RoutePerformanceEmitter>,
): void {
  const tracker = createRoutePerformanceTracker({
    capture: captureRouteEvent,
    now: () => performance.now(),
    afterPaint: afterNextPaint,
    schedule: scheduleTimeout,
    ...emitterOverrides,
  });
  activeTracker = tracker;

  // The href we last committed a route_rendered for. invalidate()/load of the
  // current location can still report hrefChanged when resolvedLocation lags
  // latestLocation; comparing against this keeps those from looking like a
  // new client navigation (which would then be "cancelled" by the next push).
  let lastRenderedHref: string | undefined;

  router.subscribe('onBeforeNavigate', (event) => {
    // router.load() re-emits this for invalidations and reloads of the current
    // location (hrefChanged false) and for the initial document load (no
    // fromLocation yet). Neither is a client navigation — treating them as one
    // resets the tracker mid-flight and swallows the real navigation's events.
    if (!event.hrefChanged || event.fromLocation == null) return;
    if (event.toLocation.href === lastRenderedHref) return;
    const [routes] = router.getMatchedRoutes(event.toLocation.pathname);
    tracker.onBeforeNavigate(
      leafRouteInfo(
        router,
        routes.map((route) => ({ routeId: route.id })),
      ),
    );
  });
  router.subscribe('onRendered', (event) => {
    lastRenderedHref = event.toLocation.href;
    tracker.onRendered(
      leafRouteInfo(router, router.state.matches) ?? UNKNOWN_ROUTE,
    );
  });
}
